import { ShieldCheck } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { z } from "zod";
import { Button, FormField, Input } from "@/components/ui/controls";
import { Dialog } from "@/components/ui/overlays";
import { ApiError, apiRequest, jsonBody } from "@/lib/api/client";
import { StepUpContext } from "./step-up-context";
import styles from "./step-up-provider.module.css";

type Resolver = { resolve: () => void; reject: (reason: Error) => void };

export function StepUpProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);
  const resolver = useRef<Resolver | null>(null);

  const verify = useCallback(
    () =>
      new Promise<void>((resolve, reject) => {
        resolver.current = { resolve, reject };
        setCode("");
        setError("");
        setOpen(true);
      }),
    [],
  );

  const run = useCallback(
    async <T,>(operation: () => Promise<T>) => {
      try {
        return await operation();
      } catch (reason) {
        if (
          !(reason instanceof ApiError) ||
          reason.code !== "step_up_required"
        ) {
          throw reason;
        }
        await verify();
        return operation();
      }
    },
    [verify],
  );

  function close() {
    resolver.current?.reject(
      new ApiError("step_up_cancelled", "İşlem iptal edildi."),
    );
    resolver.current = null;
    setOpen(false);
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setPending(true);
    setError("");
    try {
      await apiRequest("auth/step-up/", z.object({ verified_at: z.string() }), {
        method: "POST",
        ...jsonBody({ code }),
      });
      resolver.current?.resolve();
      resolver.current = null;
      setOpen(false);
    } catch (reason) {
      setError(
        reason instanceof ApiError ? reason.message : "Kod doğrulanamadı.",
      );
    } finally {
      setPending(false);
    }
  }

  return (
    <StepUpContext.Provider value={{ run }}>
      {children}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Kimliğinizi doğrulayın"
        description="Bu işlemi tamamlamak için doğrulayıcı uygulamanızdaki güncel kodu girin."
        size="sm"
        footer={
          <>
            <Button variant="secondary" onClick={close}>Vazgeç</Button>
            <Button
              type="submit"
              form="step-up-form"
              disabled={pending || code.length !== 6}
            >
              <ShieldCheck size={17} />
              {pending ? "Doğrulanıyor…" : "Doğrula ve devam et"}
            </Button>
          </>
        }
      >
        <form id="step-up-form" className={styles.form} onSubmit={submit}>
          {error ? <div className={styles.error} role="alert">{error}</div> : null}
          <FormField label="6 haneli kod" htmlFor="step-up-code" required>
            <Input
              autoFocus
              id="step-up-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(event) =>
                setCode(event.target.value.replace(/\D/g, ""))
              }
            />
          </FormField>
        </form>
      </Dialog>
    </StepUpContext.Provider>
  );
}
